'use client';

import { Collapse as AntCollapse, Col, ConfigProvider, Flex, Row } from 'antd';
import React from 'react';

import { AppColors } from '../../theme';
import Icon from '../icon/icon';
import Tag from '../tag/tag';
import styles from './collapse.styles';

const ExpandIcon = (rotate: number) => (
  <Icon
    className='bi-chevron-down'
    style={{ ...styles.collapseIcon, transform: `rotate(${rotate}deg)` }}
  />
);

const Header = ({
  name,
  sku,
  total,
  pending,
  committed,
  prepared,
  delivered,
  statuses,
  children,
  isMultippleDeliveryDate,
  isFullyDelivered,
  isDelivered
}: {
  name: string;
  sku: string;
  total?: string;
  pending?: number;
  committed?: number;
  prepared?: number;
  delivered?: number;
  statuses?: Array<{
    id: string;
    name: string;
    count: number;
  }>;
  children: React.ReactNode;
  isMultippleDeliveryDate: boolean;
  isFullyDelivered: boolean;
  isDelivered: boolean;
}) => (
  <>
    {children}
    <Row gutter={[0, 0]} style={styles.collapseHeader__row}>
      <Col>
        <Row gutter={[0, 0]} style={styles.collapseHeader__subtitle}>
          <span className='body-small'>{sku}</span>
        </Row>
        <Row gutter={[0, 0]} style={styles.collapseHeader__title}>
          <h5 className='font-semibold'>{name}</h5>
        </Row>
        <Flex gap={8} wrap='wrap'>
          {total !== undefined && <Tag type='primary'>{total}</Tag>}

          {statuses ? (
            <>
              {statuses.map((status) => {
                if (status.count === 0) return null;

                if (status.id === 'DELIVERED') {
                  return (
                    <Tag type='success' key={status.id}>
                      {status.name} ({status.count})
                    </Tag>
                  );
                }

                if (!isDelivered && !isFullyDelivered) {
                  return (
                    <Tag type='default' key={status.id}>
                      {status.name} ({status.count})
                    </Tag>
                  );
                }

                return null;
              })}
            </>
          ) : (
            <>
              {!isDelivered && !isFullyDelivered && (
                <>
                  {pending !== undefined && pending > 0 && (
                    <Tag type='default'>Pendiente ({pending})</Tag>
                  )}
                  {committed !== undefined && committed > 0 && (
                    <Tag type='default'>Comprometido ({committed})</Tag>
                  )}
                  {prepared !== undefined && prepared > 0 && (
                    <Tag type='default'>Preparado ({prepared})</Tag>
                  )}
                </>
              )}
              {delivered !== undefined && delivered > 0 && (
                <Tag type='success'>Entregado ({delivered})</Tag>
              )}
            </>
          )}
        </Flex>
      </Col>
    </Row>
  </>
);

export interface CollapseProps {
  /**
   * Un array de items para ser renderizados como paneles colapsables.
   */
  items: Array<{
    /**
     * Un identificador único para el item.
     */
    id: string;
    /**
     * Determina el comportamiento del disparador de colapso.
     * 'icon': El panel se puede expandir o colapsar haciendo clic en el ícono.
     * 'disabled': El panel no se puede expandir o colapsar.
     */
    collapsible: 'icon' | 'disabled';
    /**
     * El nombre o título principal que se mostrará en el encabezado.
     */
    name: string;
    /**
     * El SKU o código de referencia para el item.
     */
    sku: string;
    /**
     * Un nodo de React para mostrar la información de la fecha de entrega.
     */
    deliverayDate: React.ReactNode;
    /**
     * La cantidad o monto total, que se muestra como una etiqueta principal.
     */
    total?: string;
    /**
     * El número de items pendientes, que se muestra en una etiqueta.
     */
    pending?: number;
    /**
     * El número de items comprometidos, que se muestra en una etiqueta.
     */
    committed?: number;
    /**
     * El número de items preparados, que se muestra en una etiqueta.
     */
    prepared?: number;
    /**
     * El número de items entregados, que se muestra en una etiqueta de éxito.
     */
    delivered?: number;
    /**
     * Arreglo de estados dinámicos del producto.
     */
    statuses?: Array<{
      id: string;
      name: string;
      count: number;
    }>;
    /**
     * El contenido que se mostrará dentro del panel colapsable.
     */
    children?: React.ReactNode;
    /**
     * Bandera que indica si el item tiene múltiples fechas de entrega.
     */
    isMultippleDeliveryDate: boolean;
    /**
     * Bandera que indica si el item está completamente entregado.
     */
    isFullyDelivered: boolean;
    /**
     * Bandera que indica si el item está entregado.
     */
    isDelivered: boolean;
  }>;
}

/**
 * Un componente de Collapse personalizado que muestra una lista de items colapsables con encabezados detallados.
 * Está construido sobre el componente Collapse de Ant Design y está diseñado para esta aplicación específica.
 *
 * @param {CollapseProps} props - Las props para el componente Collapse.
 *
 * @returns {JSX.Element} El componente Collapse renderizado.
 */
const Collapse = ({ items }: CollapseProps) => {
  const itemProps = items.map((item) => ({
    key: `${item.id}`,
    styles: {
      header: styles.collapseHeader,
      body: styles.collapseBody
    },
    label: Header({
      name: item.name,
      sku: item.sku,
      total: item.total,
      pending: item.pending,
      delivered: item.delivered,
      committed: item.committed,
      prepared: item.prepared,
      statuses: item.statuses,
      children: item.deliverayDate,
      isMultippleDeliveryDate: item.isMultippleDeliveryDate,
      isFullyDelivered: item.isFullyDelivered,
      isDelivered: item.isDelivered
    }),
    children: item.children,
    collapsible: item.collapsible
  }));

  return (
    <ConfigProvider
      theme={{
        components: {
          Collapse: {
            headerBg: 'transparent',
            headerPadding: 0,
            contentBg: AppColors.VA.NEUTRALS.WHITE,
            contentPadding: 0
          }
        }
      }}>
      <AntCollapse
        accordion={true}
        bordered={false}
        ghost
        expandIconPosition='end'
        expandIcon={({ isActive }) => ExpandIcon(isActive ? 180 : 0)}
        items={itemProps}
      />
    </ConfigProvider>
  );
};

export default Collapse;
